import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { useCompatibility } from "./useCompatibility";
import { MatchItem } from "./types";
import {
  Heart,
  Sparkles,
  Users,
  Calendar,
  Award,
  Mail,
  MapPin,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  UserPlus,
  Loader2,
  TrendingUp,
  MessageCircle,
} from "lucide-react";

export const MatchesList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    loading,
    matches,
    hasProfile,
    checkUserProfile,
    fetchMatches,
    sendConnectionRequest,
  } = useCompatibility(user?.hasCompatibilityProfile);

  const [sending, setSending] = useState<string | null>(null);
  const [hasFetchedMatches, setHasFetchedMatches] = useState<boolean>(false);

  const handleCardClick = (item: MatchItem) => {
    if (item.connectionStatus === "accepted" && item.requestId) {
      navigate(`/compatibility-synastry/${item.requestId}`, {
        state: { otherUserName: item.userId.username },
      });
    } else {
      navigate("/history");
    }
  };

  useEffect(() => {
    if (user?.hasCompatibilityProfile === undefined) {
      checkUserProfile().catch(() =>
        toast.error("Failed to check your profile status."),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasProfile) {
      fetchMatches()
        .catch(() => toast.error("Failed to load matches."))
        .finally(() => setHasFetchedMatches(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProfile]);

  const handleConnect = async (userId: string, name: string) => {
    setSending(userId);
    try {
      await sendConnectionRequest(userId);
      toast.success(`✨ Request sent to ${name}!`);
      await fetchMatches();
    } catch (error) {
      toast.error("Failed to send request. Please try again.");
    } finally {
      setSending(null);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return "text-emerald-500";
    if (score >= 60) return "text-amber-500";
    return "text-rose-400";
  };

  const getScoreBackground = (score: number): string => {
    if (score >= 80) return "bg-emerald-50 border-emerald-200";
    if (score >= 60) return "bg-amber-50 border-amber-200";
    return "bg-rose-50 border-rose-200";
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="w-4 h-4 text-amber-500" />;
      case "accepted":
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case "rejected":
        return <XCircle className="w-4 h-4 text-rose-400" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case "pending":
        return "Pending";
      case "accepted":
        return "Connected";
      case "rejected":
        return "Declined";
      default:
        return "";
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "pending":
        return "bg-amber-100 text-amber-700 border-amber-200";
      case "accepted":
        return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "rejected":
        return "bg-rose-100 text-rose-700 border-rose-200";
      default:
        return "";
    }
  };

  if (!hasProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-rose-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl"></div>

        <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-12 text-center max-w-md relative z-10 shadow-2xl shadow-rose-100/50 border border-white/50">
          <div className="w-24 h-24 bg-gradient-to-r from-rose-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-12 h-12 text-rose-400" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Complete Your Profile
          </h2>
          <p className="text-gray-500 mb-8 leading-relaxed">
            To find your perfect match, we need to understand your unique
            essence. Complete your compatibility profile and begin your journey.
          </p>
          <Link
            to="/compatibility-form"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-rose-400 to-purple-400 text-white font-semibold rounded-2xl shadow-lg shadow-rose-200 hover:shadow-xl hover:shadow-rose-300 hover:scale-105 transition-all duration-200"
          >
            <Sparkles className="w-5 h-5" />
            Begin Your Journey
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 p-4 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-rose-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-6 mb-6 shadow-lg shadow-rose-100/30 border border-white/50">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="bg-gradient-to-r from-rose-400 to-purple-400 p-2 rounded-xl shadow-lg shadow-rose-200">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Your Matches
                </h1>
              </div>
              <p className="text-sm text-gray-500 ml-11">
                {matches.length > 0
                  ? `✨ You have ${matches.length} potential connection${matches.length > 1 ? "s" : ""} waiting`
                  : "🌟 Your perfect match is on the way"}
              </p>
            </div>
            <Link
              to="/history"
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-400 to-purple-400 text-white font-medium rounded-xl shadow-lg shadow-rose-200 hover:shadow-xl hover:shadow-rose-300 hover:scale-105 transition-all duration-200"
            >
              <Clock className="w-4 h-4" />
              Connection History
            </Link>
          </div>
        </div>

        {/* Loading State */}
        {(!hasFetchedMatches || loading) && matches.length === 0 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-16 text-center shadow-lg shadow-rose-100/30 border border-white/50">
            <div className="flex flex-col items-center justify-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-rose-200 rounded-full"></div>
                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-rose-400 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <span className="mt-4 text-gray-500 font-medium">
                Finding your cosmic matches...
              </span>
              <span className="text-sm text-gray-400 mt-1">
                This may take a moment
              </span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {hasFetchedMatches && !loading && matches.length === 0 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-16 text-center shadow-lg shadow-rose-100/30 border border-white/50">
            <div className="w-24 h-24 bg-gradient-to-r from-rose-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-12 h-12 text-rose-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              No Matches Yet
            </h3>
            <p className="text-gray-500 max-w-md mx-auto leading-relaxed">
              The universe is still aligning the stars for you. Check back soon
              — your perfect match is on their way!
            </p>
          </div>
        )}

        {/* Matches Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {matches.map((item: MatchItem) => {
            const score = item.matchScore;
            const isHighScore = score >= 70;

            return (
              <div
                key={item.userId._id}
                className={`group bg-white/80 backdrop-blur-xl rounded-3xl p-5 shadow-lg shadow-rose-100/30 border border-white/50 hover:shadow-2xl hover:shadow-rose-200/50 transition-all duration-300 hover:scale-[1.02] cursor-pointer relative overflow-hidden`}
                onClick={() => handleCardClick(item)}
              >
                {/* Score indicator ring */}
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br from-rose-200/20 to-purple-200/20 blur-2xl"></div>

                {/* High score badge */}
                {isHighScore && (
                  <div className="absolute top-4 right-4 z-10">
                    <div className="bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg shadow-amber-200 flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" />
                      High Match
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div
                        className={`w-14 h-14 rounded-2xl bg-gradient-to-r from-rose-400 to-purple-400 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-rose-200 group-hover:shadow-xl group-hover:shadow-rose-300 transition-all duration-200`}
                      >
                        {item.userId.username.charAt(0).toUpperCase()}
                      </div>
                      {item.connectionStatus === "accepted" && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-400 rounded-full border-2 border-white flex items-center justify-center">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 group-hover:text-rose-400 transition-colors">
                        {item.userId.username}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <Mail className="w-3 h-3" />
                        <span className="truncate max-w-[100px]">
                          {item.userId.email}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 ${getScoreBackground(score)}`}
                  >
                    <TrendingUp className={`w-4 h-4 ${getScoreColor(score)}`} />
                    <span
                      className={`text-lg font-bold ${getScoreColor(score)}`}
                    >
                      {score}%
                    </span>
                  </div>
                </div>

                {/* Compatibility details */}
                <div className="bg-gradient-to-br from-gray-50 to-rose-50/30 rounded-2xl p-4 mb-4 border border-gray-100/50">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-rose-400" />
                      <span className="text-xs text-gray-600">
                        Age: {item.matchDetails.ageCompatibility}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-purple-400" />
                      <span className="text-xs text-gray-600">
                        Values: {item.matchDetails.valuesMatch}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-rose-400" />
                      <span className="text-xs text-gray-600">
                        Interests: {item.matchDetails.interestsMatch}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      <span className="text-xs text-gray-600">
                        Personality: {item.matchDetails.personalityMatch}
                      </span>
                    </div>
                  </div>
                </div>

                {item.connectionStatus !== "none" ? (
                  <div
                    className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border ${getStatusColor(item.connectionStatus)}`}
                  >
                    {getStatusIcon(item.connectionStatus)}
                    <span className="text-sm font-medium">
                      {getStatusLabel(item.connectionStatus)}
                    </span>
                    {item.connectionStatus === "accepted" && (
                      <MessageCircle className="w-4 h-4 ml-1" />
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-rose-400 to-purple-400 text-white font-medium rounded-xl shadow-lg shadow-rose-200 hover:shadow-xl hover:shadow-rose-300 hover:scale-[1.02] active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConnect(item.userId._id, item.userId.username);
                    }}
                    disabled={sending === item.userId._id}
                  >
                    {sending === item.userId._id ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-5 h-5" />
                        Connect
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MatchesList;
